import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useDeleteLeague } from "~pages/Leagues/LeagueListPage/hooks/useDeleteLeague.ts";
import { useFilteredLeagues } from "~pages/Leagues/LeagueListPage/hooks/useFilteredLeagues.ts";
import { useGetLeagueTableColumns } from "~pages/Leagues/LeagueListPage/hooks/useGetLeagueTableColumns.tsx";
import { useGetLeagueTableFilters } from "~pages/Leagues/LeagueListPage/hooks/useGetLeagueTableFilters.tsx";
import type { League } from "~types/league.ts";

const LeagueListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/leagues/manage/`);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.leagues.listPage;
  const filterConfig = useGetLeagueTableFilters();

  const { filteredLeagues, isLoading, searchText, setSearchText } = useFilteredLeagues({
    regionTypeFilter: filterConfig.currentFilter.regionType,
    countryCodeFilter: filterConfig.currentFilter.countryCode,
  });

  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteLeague();

  const handleDelete = useCallback(
    async (leagueId: string) => {
      return await deleteFn(leagueId);
    },
    [deleteFn],
  );

  const columns = useGetLeagueTableColumns();

  return (
    <DataTable<League>
      title={formatMessage({ id: "leaguesListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.leagueId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(`/${customerId}/admin/${sport}/${organizationId}/leagues/manage/${row.leagueId}`),
          },
        },
      }}
      rowKey={"leagueId"}
      rowNameKey={"nameLocal"}
      dataSource={filteredLeagues}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.leagues`}
      isDataLoading={isLoading}
      handleAdd={handleAdd}
      addButtonIntlKey={"league"}
      filtersConfig={filterConfig}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      permissionNames={[PermissionNames.LEAGUE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, total: filteredLeagues.length }}
    />
  );
};

export default LeagueListPage;
